// @ts-check
import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { useSelector } from 'react-redux';

import { CollapsiblePanel, Icon } from 'components/common';
import { colours } from 'utils/theme';
import { getFilters } from 'selectors/organisation';
import { orgPageExcludedFilters, tokenise } from 'utils/filters';
import { GetSearchKeysByView } from 'components/Search/Filters/keys';
import { searchOrigin } from 'components/Search/Results/constants';
import FitlersCount from 'components/Search/Filters/FitlersCount';

// SCIL-17770: org-side equivalent of the main-search FilterGroup. The
// main-search one binds to the `search` slice (collapse state, tokenise against
// the researcher/organisation views); this reads the shared org `filters` slice
// and tokenises against the organisation-page view. Collapse is local state —
// org filters do not persist section collapse today and adding a slice for it
// would be scope creep.

const Container = styled.div`
  font-size: 14px;
  border-bottom: 1px solid ${p => p.theme.lightGrey};
`;

const Title = styled.div`
  user-select: none;
  font-weight: 500;
  cursor: pointer;
  padding: 10px 0;
  display: flex;
  color: ${p => p.theme.grey};
  > div {
    width: 30px;
    display: flex;
    justify-content: center;
    align-items: center;
  }
`;

const Content = styled.div`
  padding: 0px 10px 10px;
  > * {
    margin-bottom: 10px;
    :last-child {
      margin-bottom: 5px;
    }
  }
`;

const RotatingIconWithProps = ({ up, children, ...rest }) => (
  <div {...rest}>{children}</div>
);

const RotatingIcon = styled(RotatingIconWithProps)`
  margin-left: auto;
  transform: ${p => (p.up ? 'rotate(180deg)' : 'rotate(0)')};
  transition: transform 0.2s linear;
`;

/**
 * @param {{
 *   organisationId: number,
 *   title: React.ReactNode,
 *   icon: string,
 *   activeKeys: string[],
 *   children: React.ReactNode,
 *   defaultCollapsed?: boolean,
 * }} props
 */
const OrgFilterSection = ({
  organisationId,
  title,
  icon,
  activeKeys,
  children,
  defaultCollapsed = true,
}) => {
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);
  const filters = useSelector(getFilters(organisationId));

  const activeFilters = useMemo(() => {
    if (!activeKeys?.length || !filters) return {};
    return activeKeys.reduce((acc, current) => {
      if (filters[current] != null) acc[current] = filters[current];
      return acc;
    }, {});
  }, [activeKeys, filters]);

  const tokens = tokenise(
    activeFilters,
    GetSearchKeysByView(searchOrigin.organisationPageSearch),
    orgPageExcludedFilters
  );

  return (
    <Container data-testid={`org-filter-group-${icon}`}>
      <Title
        data-testid='org-filter-group-title'
        onClick={() => setIsCollapsed(!isCollapsed)}
      >
        <div>
          {/* @ts-ignore */}
          <Icon icon={icon} color={colours.darkGrey} size='sm' />
        </div>
        {title}
        <FitlersCount count={tokens?.length} />
        <RotatingIcon up={!isCollapsed}>
          {/* @ts-ignore */}
          <Icon icon={'chevronDown'} size='sm' color={colours.darkGrey} />
        </RotatingIcon>
      </Title>
      <CollapsiblePanel isCollapsed={isCollapsed}>
        <Content>{children}</Content>
      </CollapsiblePanel>
    </Container>
  );
};

export default OrgFilterSection;
